import { API_BASE_URL, APP_HEADERS } from '../../lib/env';

/**
 * The admin SPA's own sign-in and sign-out (owner decision 2026-09-26: the
 * admin has a login, no registration, and only an admin can pass it). Both
 * hit apps/api `auth/admin/*`, which sets / clears the NextAuth session cookie
 * everything else already reads — see admin-session.controller.ts.
 *
 * Plain fetch rather than `apiFetch`: that one attaches a CSRF token minted by
 * an ADMIN-only endpoint, which by definition cannot be reached before this
 * sign-in. The API protects the login by requiring a JSON body instead.
 */

export type AdminSignInResult =
  | { kind: 'ok' }
  | { kind: 'invalid' }
  | { kind: 'throttled'; retryAfterSec: number }
  | { kind: 'error' };

export async function signInAdmin(email: string, password: string): Promise<AdminSignInResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...APP_HEADERS, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    return { kind: 'error' };
  }

  if (response.ok) return { kind: 'ok' };
  if (response.status === 401) return { kind: 'invalid' };
  if (response.status === 429) {
    const body = (await response.json().catch(() => ({}))) as { retryAfterSec?: unknown };
    const retryAfterSec = typeof body.retryAfterSec === 'number' ? body.retryAfterSec : 60;
    return { kind: 'throttled', retryAfterSec };
  }
  return { kind: 'error' };
}

export async function signOutAdmin(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/admin/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: APP_HEADERS,
    });
  } catch {
    // Offline: the redirect to /login below still happens; the cookie expires on its own.
  }
}

/** Only same-app paths survive as a post-login target — never an absolute URL (open redirect). */
export function safeNextPath(raw: unknown): string {
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && raw !== '/login'
    ? raw
    : '/';
}
